import React, { useEffect, useState } from 'react';
import {
    Scale, Clock, CheckCircle2, TrendingUp,
    Calendar, FileText, ArrowUpRight, AlertTriangle,
    Plus, Loader2, BarChart3, DollarSign
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { api } from '@/lib/api';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@/hooks/useTranslation';

const StatCard = ({ title, value, icon: Icon, subtitle, color }) => (
    <Card className="overflow-hidden border-0 bg-white shadow-md hover:shadow-xl transition-all duration-300 group">
        <CardContent className="p-6">
            <div className="flex items-center justify-between">
                <div className="space-y-1">
                    <p className="text-sm text-gray-500 font-medium">{title}</p>
                    <p className="text-3xl font-bold text-[#193D6D]">{value}</p>
                    {subtitle && (
                        <p className="text-xs text-gray-400 mt-1">{subtitle}</p>
                    )}
                </div>
                <div className={`p-3.5 rounded-2xl ${color} transition-all duration-300 group-hover:scale-110`}>
                    <Icon className="w-6 h-6 text-white" />
                </div>
            </div>
        </CardContent>
    </Card>
);

const statusLabels = {
    'aguardando_perito': { label: 'Aguardando', color: 'bg-amber-100 text-amber-700' },
    'em_andamento': { label: 'Em Andamento', color: 'bg-blue-100 text-blue-700' },
    'laudo_entregue': { label: 'Laudo Entregue', color: 'bg-emerald-100 text-emerald-700' },
    'concluido': { label: 'Concluído', color: 'bg-green-100 text-green-700' },
    'arquivado': { label: 'Arquivado', color: 'bg-gray-100 text-gray-600' },
};

export default function SystemDashboard() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const { currentLanguage } = useTranslation();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const getPath = (path) => `/${currentLanguage}${path}`;

    useEffect(() => {
        const load = async () => {
            try {
                const result = await api.dashboard.get();
                setData(result);
            } catch (err) {
                console.error('Dashboard load error:', err);
                setError(err.error || 'Erro ao carregar dashboard');
                // Provide fallback data for demo mode
                setData({
                    stats: { processos_ativos: 14, laudos_pendentes: 3, concluidos: 42, total_honorarios: 154000 },
                    recent_cases: [
                        { id: 1, numero: '0012345-67.2023.8.26.0100', vara: '2ª Vara Cível', status: 'em_andamento', prazo_entrega: '2024-05-15' },
                        { id: 2, numero: '0054321-12.2023.8.26.0100', vara: '5ª Vara da Fazenda', status: 'aguardando_perito', prazo_entrega: '2024-06-20' },
                        { id: 3, numero: '0098765-43.2023.8.19.0001', vara: '1ª Vara Empresarial', status: 'laudo_entregue', prazo_entrega: '2024-04-10' },
                    ],
                    deadlines: [
                        { id: 1, numero: '0012345-67.2023.8.26.0100', vara: '2ª Vara Cível', prazo_entrega: '2024-05-15' },
                    ]
                });
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <Loader2 className="h-10 w-10 animate-spin text-[#193D6D]" />
            </div>
        );
    }

    const stats = data?.stats || {};
    const formatCurrency = (val) => {
        if (val >= 1000) return `R$ ${(val / 1000).toFixed(1)}k`;
        return `R$ ${val}`;
    };

    return (
        <div className="space-y-8 p-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-[#193D6D] tracking-tight mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
                        Painel de Controle
                    </h1>
                    <p className="text-gray-500">
                        Bem-vindo, <span className="font-semibold text-[#193D6D]">{user?.nome || user?.email?.split('@')[0] || 'Administrador'}</span>.
                        Aqui está o resumo das suas perícias.
                    </p>
                </div>
                <div className="flex gap-3">
                    <Button
                        variant="outline"
                        onClick={() => navigate(getPath('/sistema/agenda'))}
                        className="border-[#193D6D]/20 text-[#193D6D] hover:bg-[#193D6D]/5"
                    >
                        <Calendar className="mr-2 h-4 w-4" /> Agenda
                    </Button>
                    <Button
                        onClick={() => navigate(getPath('/sistema/processos'))}
                        className="bg-[#193D6D] hover:bg-[#0F2847] text-white shadow-lg shadow-[#193D6D]/20"
                    >
                        <Plus className="mr-2 h-4 w-4" /> Novo Processo
                    </Button>
                </div>
            </div>

            {error && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <div>
                        <p className="text-sm font-semibold text-amber-800">Modo Demonstração</p>
                        <p className="text-xs text-amber-600">Não foi possível se conectar à API. Exibindo dados de exemplo.</p>
                    </div>
                </div>
            )}

            {/* Stats Cards */}
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
                <StatCard title="Processos Ativos" value={stats.processos_ativos} icon={Scale} color="bg-[#193D6D]" subtitle="Em andamento" />
                <StatCard title="Laudos Pendentes" value={stats.laudos_pendentes} icon={Clock} color="bg-amber-500" subtitle="Aguardando entrega" />
                <StatCard title="Concluídos" value={stats.concluidos} icon={CheckCircle2} color="bg-emerald-500" subtitle="Total acumulado" />
                <StatCard title="Honorários" value={formatCurrency(stats.total_honorarios)} icon={DollarSign} color="bg-[#FCD34D] !text-[#193D6D]" subtitle="Total estimado" />
            </div>

            {/* Two-column layout */}
            <div className="grid gap-8 md:grid-cols-7">
                {/* Recent Cases */}
                <Card className="md:col-span-4 border-0 shadow-md">
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                        <CardTitle className="text-lg font-bold text-[#193D6D] flex items-center gap-2">
                            <FileText className="w-5 h-5 text-[#FCD34D]" />
                            Nomeações Recentes
                        </CardTitle>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(getPath('/sistema/processos'))}
                            className="text-[#193D6D] hover:text-[#FCD34D] group"
                        >
                            Ver todos
                            <ArrowUpRight className="ml-1 w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                        </Button>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            {(data?.recent_cases || []).map((c) => {
                                const st = statusLabels[c.status] || { label: c.status, color: 'bg-gray-100 text-gray-600' };
                                return (
                                    <div
                                        key={c.id}
                                        onClick={() => navigate(getPath(`/sistema/processos`))}
                                        className="group flex items-center justify-between p-4 rounded-xl border border-transparent hover:border-[#193D6D]/10 hover:bg-[#193D6D]/[0.02] transition-all cursor-pointer"
                                    >
                                        <div className="space-y-1">
                                            <p className="font-mono text-sm font-semibold text-[#193D6D]">{c.numero}</p>
                                            <p className="text-xs text-gray-400 flex items-center gap-2">
                                                <span className="font-medium text-gray-500">{c.vara}</span>
                                                {c.prazo_entrega && (
                                                    <>
                                                        <span className="w-1 h-1 rounded-full bg-gray-300"></span>
                                                        <span>Prazo: {new Date(c.prazo_entrega).toLocaleDateString('pt-BR')}</span>
                                                    </>
                                                )}
                                            </p>
                                        </div>
                                        <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${st.color}`}>
                                            {st.label}
                                        </span>
                                    </div>
                                );
                            })}
                            {(!data?.recent_cases || data.recent_cases.length === 0) && (
                                <p className="text-center text-gray-400 py-8">Nenhum processo encontrado.</p>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* Deadlines */}
                <Card className="md:col-span-3 border-0 shadow-md overflow-hidden">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-lg font-bold text-[#193D6D] flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-[#FCD34D]" />
                            Alertas de Prazos
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                        {(data?.deadlines || []).length > 0 ? (
                            <>
                                <div className="bg-amber-50 p-5 border-y border-amber-100">
                                    <p className="text-xs font-bold text-amber-600 uppercase tracking-widest mb-1">Próximos vencimentos</p>
                                    {data.deadlines.map((d) => (
                                        <div key={d.id} className="flex items-start gap-3 mt-3">
                                            <div className="mt-1 w-2 h-2 rounded-full bg-amber-500 flex-shrink-0"></div>
                                            <div>
                                                <p className="text-sm font-semibold text-[#193D6D]">{d.numero}</p>
                                                <p className="text-xs text-gray-500">
                                                    {d.vara} • Prazo: {new Date(d.prazo_entrega).toLocaleDateString('pt-BR')}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </>
                        ) : (
                            <div className="p-8 text-center">
                                <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
                                <p className="text-sm font-medium text-gray-500">Nenhum prazo urgente</p>
                                <p className="text-xs text-gray-400 mt-1">Todos os prazos estão em dia.</p>
                            </div>
                        )}

                        <div className="p-5 space-y-3">
                            <Button
                                onClick={() => navigate(getPath('/sistema/agenda'))}
                                className="w-full bg-[#FCD34D] text-[#193D6D] hover:bg-[#FBBF24] font-bold border-none shadow-sm"
                            >
                                Ver Cronograma Completo
                            </Button>
                            <Button
                                variant="outline"
                                onClick={() => navigate(getPath('/sistema/relatorios'))}
                                className="w-full border-[#193D6D]/15 text-[#193D6D] hover:bg-[#193D6D]/5"
                            >
                                <BarChart3 className="mr-2 h-4 w-4" />
                                Ver Relatórios
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
